import {
  Avatar,
  Button,
  Colors,
  Divider,
  Flex,
  Menu,
  TopBar,
} from '@cognite/cogs.js';
import { Dropdown, MenuProps } from 'antd';
import { Icon } from 'components/Icon';
import { useStoreActions, useStoreState } from 'hooks';
import { useStatefulHistory } from 'hooks/history';
import { useAccessControl, useGetPermissions } from 'hooks/useAccessControl';
import { useUrl } from 'hooks/useUrls';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_KEY_LOCALSTORAGE_KEY } from 'store/auth';
import styled from 'styled-components/macro';
import { useGetDigitalTwinNameLazyQuery } from 'types/dt-graphql-apollo-v2';
import { URL_ACADEMY, URL_DOCS, URL_POLICY } from 'utils/config';
import { FeatureFlags, useFeatureFlag } from 'utils/feature-flags';
import { getFirstLetterFromEmail, getSecondLetterFromEmail } from 'utils/misc';
import mixpanel from 'utils/mixpanel-frontend';
import { getDigitalTwinUrl } from 'utils/router-url';
import { NewDigitalTwin } from '../../pages/AllDigitalTwinsPage/components/NewDigitalTwin';
type CogsNavigationLink = {
  name: string;
  isActive: boolean;
  onClick: () => void;
};

const DIGITAL_TWINS = 'Project management';
const DATA_CATALOG = 'Data catalogue';
const DATA_QUALITY_EXPLORER = 'Data quality';
const DATA_CONSISTENCY = 'Data consistency';

type SimpleTab = {
  slug: string;
  title: string;
};

export const Header = () => {
  const [newDigitalTwin, setNewDigitalTwin] = useState(false);
  const [showDropdown, setShowDropdown] = useState<string>('');
  const history = useStatefulHistory();

  const enabledDigitalTwins = useFeatureFlag(FeatureFlags.enabledDigitalTwins);
  const enabledDataQualityExplorer = useFeatureFlag(
    FeatureFlags.enabledDataQualityExplorer
  );
  const enabledDataConsistency = useFeatureFlag(
    FeatureFlags.enabledDataConsistency
  );
  const enabledAccessControl = useFeatureFlag(
    FeatureFlags.enabledAccessControl
  );

  const tenant = useUrl.Tenant();
  const email = useStoreState((state) => state.auth.email);

  const setLoggedIn = useStoreActions((actions) => actions.auth.setLoggedIn);
  const setPermissions = useStoreActions(
    (actions) => actions.permissions.setPermissions
  );

  const { getPermissions } = useGetPermissions();
  const hasAccess = useAccessControl();
  const canCreateProject = hasAccess(['Create_DigitalTwin']);

  const params = useParams<{
    page: string;
    digitalTwinId: string;
    projectId: string;
  }>();

  const [getDigitalTwinName, { data }] = useGetDigitalTwinNameLazyQuery();

  useEffect(() => {
    if (params.digitalTwinId) {
      getDigitalTwinName({
        variables: {
          id: params.digitalTwinId,
        },
      });
    }
  }, [params.digitalTwinId]);

  useEffect(() => {
    const fetchPermissions = async () => {
      const permissionsFetched = await getPermissions();
      setPermissions(permissionsFetched || []);
    };

    fetchPermissions();
  }, []);

  const closeDropdown = () => setShowDropdown('');

  const onLogOut = async () => {
    setLoggedIn(false);
    window.localStorage.removeItem('@cognite/sdk:oidAuthCodeRedirect');
    window.localStorage.removeItem(API_KEY_LOCALSTORAGE_KEY);
    history.pushStateful('/');
  };

  const redirectProjectManagement = () => {
    closeDropdown();
    history.pushStateful(`/${tenant}/`);
  };

  const redirectAccessControl = () => {
    mixpanel.track(`Opened Access Control`, {
      tenant,
      project: params.digitalTwinId,
    });
    closeDropdown();
    history.pushStateful(`/${tenant}/access-control`);
  };

  const redirectHome = () => {
    closeDropdown();
    history.pushStateful(`/${tenant}`);
  };

  const projectManagementTabs: Array<SimpleTab> = [
    {
      slug: 'schemas',
      title: DATA_CATALOG,
    },
  ];

  // Handle the Data Consistency tab
  if (enabledDataConsistency) {
    projectManagementTabs.push({
      slug: 'data-consistency',
      title: DATA_CONSISTENCY,
    });
  }

  // Handle the Data Quality Explorer tab
  if (enabledDataQualityExplorer) {
    projectManagementTabs.push({
      slug: 'data-quality',
      title: DATA_QUALITY_EXPLORER,
    });
  }

  const projectManagementLinks: CogsNavigationLink[] =
    projectManagementTabs.map((tab) => ({
      name: tab.title,
      isActive:
        params.page === tab.slug ||
        (tab.slug === '' && params.page === undefined),
      onClick: () => {
        closeDropdown();
        history.pushStateful(
          `${getDigitalTwinUrl(tenant, params.digitalTwinId)}/${tab.slug}`
        );
        mixpanel.track(`Nav: Project management/Tab/${tab.slug}`, {
          projectName: params.digitalTwinId,
        });
      },
    }));

  const items: MenuProps['items'] = [
    {
      key: '0',
      label: (
        <Flex justifyContent="flex-start" gap={8}>
          <Icon type="List" /> View all projects
        </Flex>
      ),
      onClick: redirectProjectManagement,
    },
    ...(canCreateProject
      ? [
          {
            key: '1',
            label: (
              <Flex justifyContent="flex-start" gap={8}>
                <Icon type="AddLarge" /> Create project
              </Flex>
            ),
            onClick: () => {
              closeDropdown();
              setNewDigitalTwin(true);
            },
          },
        ]
      : []),
  ];

  const renderLinks = () => {
    if (history.location.pathname.includes(`${params.digitalTwinId}`)) {
      return (
        <>
          <StyledTopBarLogo
            logo={
              <StyledTopBarItemLogo onClick={redirectHome}>
                <StyledGraphicLogo type="Cognite" />
              </StyledTopBarItemLogo>
            }
            title="Galactica"
            subtitle={
              <Dropdown
                trigger={['click']}
                open={showDropdown === 'projectManagement'}
                onOpenChange={() =>
                  setShowDropdown((current) =>
                    current !== 'projectManagement' ? 'projectManagement' : ''
                  )
                }
                menu={{ items }}
              >
                <ProjectSelectorButton
                  icon="ChevronDownSmall"
                  iconPlacement="right"
                  aria-label="project-selector"
                >
                  {data?.listDigitalTwin?.items?.[0]?.name}
                </ProjectSelectorButton>
              </Dropdown>
            }
          />
          <StyledTopBarNavigation links={projectManagementLinks} />
        </>
      );
    }
    if (
      history.location.pathname.includes('digitaltwins') &&
      enabledDigitalTwins
    ) {
      return (
        <StyledTopBarNavigation
          links={[
            {
              name: DIGITAL_TWINS,
              isActive: history.location.pathname.includes('digitaltwins'),
              onClick: redirectProjectManagement,
            },
          ]}
        />
      );
    }

    const headerLinks = [
      {
        name: 'Projects',
        isActive:
          history.location.pathname.endsWith(`/${tenant}/`) ||
          history.location.pathname.endsWith(`/${tenant}`),
        onClick: redirectProjectManagement,
      },
    ];

    // Show Access Control tab
    if (enabledAccessControl) {
      headerLinks.push({
        name: 'Access control',
        isActive: history.location.pathname.includes('access-control'),
        onClick: redirectAccessControl,
      });
    }

    return (
      <>
        <StyledTopBarLogo
          logo={
            <StyledTopBarItemLogo>
              <StyledGraphicLogo type="Cognite" />
            </StyledTopBarItemLogo>
          }
          onLogoClick={redirectHome}
          title="Galactica"
        />
        <StyledTopBarNavigation links={headerLinks} />
      </>
    );
  };

  const actions = [
    {
      key: 'support',
      component: <Icon type="Help" />,
      menu: (
        <StyledMenu>
          <SubHeading disabled>Support</SubHeading>
          <a
            href={URL_DOCS}
            style={{ color: 'black' }}
            target="_blank"
            rel="noreferrer"
          >
            <Menu.Item>Read documentation</Menu.Item>
          </a>
        </StyledMenu>
      ),
    },
    {
      key: 'account',
      component: email && (
        <Avatar
          text={`${getFirstLetterFromEmail(email)} ${getSecondLetterFromEmail(
            email
          )}`}
        />
      ),
      menu: (
        <StyledAccountMenu style={{ width: '280px' }}>
          <SubHeading disabled>Account</SubHeading>
          <StyledMenuItem disabled>
            {email && (
              <Avatar
                text={`${getFirstLetterFromEmail(
                  email
                )} ${getSecondLetterFromEmail(email)}`}
              />
            )}
            <StyledSpan>{email}</StyledSpan>
          </StyledMenuItem>
          <a
            href={URL_POLICY}
            style={{ color: 'black' }}
            target="_blank"
            rel="noreferrer"
          >
            <Menu.Item>Privacy policy</Menu.Item>
          </a>
          <Divider weight="1px" />
          <Menu.Item
            onClick={() => onLogOut()}
            icon="Logout"
            iconPlacement="right"
          >
            Log out
          </Menu.Item>
        </StyledAccountMenu>
      ),
    },
    {
      key: 'appswitcher',
      component: <Icon type="List" />,
      menu: (
        <StyledMenu style={{ width: '220px' }}>
          <span style={{ margin: '0.5rem 0 0.5rem 0.5rem' }}>Switch to</span>
          <Menu.Divider />
          <SubHeading disabled>Applications</SubHeading>
          <Menu.Item onClick={() => redirectHome()}>
            <Icon type="Cognite" /> Galactica Home
          </Menu.Item>
          <Menu.Divider />
          <SubHeading disabled>Resources</SubHeading>
          <a
            href={URL_ACADEMY}
            style={{ color: 'black' }}
            target="_blank"
            rel="noreferrer"
          >
            <Menu.Item>Cognite academy</Menu.Item>
          </a>
          <a
            href={URL_DOCS}
            style={{ color: 'black' }}
            target="_blank"
            rel="noreferrer"
          >
            <Menu.Item>ITG documentation</Menu.Item>
          </a>
        </StyledMenu>
      ),
    },
  ];

  const renderActions = () =>
    actions.map((action) => (
      <TopBar.Item key={action.key}>
        <Dropdown
          overlay={action.menu}
          trigger={['click']}
          visible={showDropdown === action.key}
          onVisibleChange={() =>
            setShowDropdown((current) =>
              current !== action.key ? action.key : ''
            )
          }
        >
          <StyledButton type="ghost" active={showDropdown === action.key}>
            {action.component}
          </StyledButton>
        </Dropdown>
      </TopBar.Item>
    ));

  return (
    <div>
      <StyledTopBar>
        <TopBar.Left>{renderLinks()}</TopBar.Left>
        <TopBar.Right>{renderActions()}</TopBar.Right>
      </StyledTopBar>
      {newDigitalTwin && (
        <NewDigitalTwin onCancel={() => setNewDigitalTwin(false)} />
      )}
    </div>
  );
};

const ProjectSelectorButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  color: var(--cogs-greyscale-grey10);
  background: var(--cogs-surface--muted) !important;
  border: none;
`;

const SubHeading = styled(Menu.Item)`
  font-size: 12px;
`;

const StyledSpan = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: calc(100% - 37px);
`;

const StyledTopBar = styled(TopBar)`
  background-color: #fff;
  height: 100%;
`;

const StyledGraphicLogo = styled(Icon)`
  width: 32px !important;
`;
const StyledTopBarItemLogo = styled(TopBar.Item)`
  padding: 0 16px;
  cursor: pointer;
  align-items: center !important;
  &:hover {
    background-color: ${Colors['decorative--blue--50']};
  }
`;

const StyledTopBarNavigation = styled(TopBar.Navigation)`
  height: 100%;
  padding-left: 0 !important;
  .navigation-item {
    border-right: 1px solid ${Colors['border--interactive--default']} !important;
    font-weight: 700 !important;
    &.active {
      height: 100% !important;
      margin-top: 0 !important;
      &:after {
        display: none;
      }
    }
  }
  .rc-tabs-tab .rc-tabs-tab-btn {
    padding: 1.2rem;
  }
`;

const StyledTopBarLogo = styled(TopBar.Logo)`
  align-items: stretch !important;
  header {
    justify-content: center !important;
  }
  .logo-title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 165px;
  }
`;

const StyledMenu = styled(Menu)`
  margin-top: -4px;
  &:active {
    color: ${Colors['decorative--grayscale--700']};
  }
`;

const StyledAccountMenu = styled(StyledMenu)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
`;

const StyledMenuItem = styled(Menu.Item)`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
`;

const StyledButton = styled(Button)<{ active: boolean }>`
  flex: 1;
  display: inline-flex;
  height: 100%;
  padding: 16px;
  border-radius: 0;
  background: ${(props) =>
    props.active ? Colors['decorative--grayscale--200'] : 'transparent'};
`;
